import React, { useEffect, useState } from "react";
import { PageContainer, PageHeader, Card } from "./Shared";
import { adminApi } from "./api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { CaretDown } from "@phosphor-icons/react";
import { runValidators, validators } from "./validators";
import ImageUpload from "./ImageUpload";

// Group → fields. Field types: text, textarea, json (a JSON list editor)
const GROUPS = [
  {
    id: "brand",
    title: "Brand & topbar",
    fields: [
      { key: "brand.tagline", label: "Brand tagline", type: "text", max: 80 },
      { key: "brand.parent",  label: "Parent line (utility bar)", type: "text", max: 120 },
    ],
  },
  {
    id: "nav",
    title: "Navigation labels",
    fields: [
      { key: "nav.about",        label: "About",         type: "text", max: 40 },
      { key: "nav.products",     label: "Products",      type: "text", max: 40 },
      { key: "nav.services",     label: "Services",      type: "text", max: 40 },
      { key: "nav.industries",   label: "Industries",    type: "text", max: 40 },
      { key: "nav.partner",      label: "Become a partner", type: "text", max: 40 },
      { key: "nav.scheduleCall", label: "Contact-us CTA", type: "text", max: 40 },
      { key: "nav.whoWeAre",     label: "About sub — Who we are", type: "text", max: 40 },
      { key: "nav.whatWeDo",     label: "About sub — What we do", type: "text", max: 40 },
      { key: "nav.whyChooseUs",  label: "About sub — Why choose us", type: "text", max: 40 },
      { key: "nav.team",         label: "Team",          type: "text", max: 40 },
      { key: "nav.careers",      label: "Careers",       type: "text", max: 40 },
      { key: "nav.clientsPortfolio", label: "Clients Portfolio", type: "text", max: 40 },
    ],
  },
  {
    id: "hero",
    title: "Hero",
    fields: [
      { key: "hero.eyebrow",       label: "Eyebrow tag",       type: "text",     max: 60 },
      { key: "hero.headline",      label: "Headline",          type: "text",     max: 120 },
      { key: "hero.sub",           label: "Subheadline",       type: "textarea", max: 280 },
      { key: "hero.cta_primary",   label: "Primary button",    type: "text",     max: 40 },
      { key: "hero.cta_secondary", label: "Secondary button",  type: "text",     max: 40 },
      { key: "hero.stats", label: "Stats (JSON list)", type: "json",
        rows: 7,
        schemaHint: '[{ "value": 150, "suffix": "+", "label": "Projects" }]',
        validateItem: (it) => (
          (it && typeof it.value !== "undefined" && typeof it.label === "string") ? "" : "Each item needs value (number) and label (string)"
        ),
      },
    ],
  },
  {
    id: "about",
    title: "About — Who we are",
    fields: [
      { key: "about.heading",  label: "Heading",  type: "text", max: 120 },
      { key: "about.body",     label: "Body",     type: "textarea", max: 800 },
      { key: "about.cta_text", label: "CTA text", type: "text", max: 40 },
      { key: "about.image_id", label: "Illustration image (right of heading — leave empty for default gradient artwork)", type: "image" },
      { key: "about.cards", label: "Pillar cards (JSON list)", type: "json", rows: 8,
        schemaHint: '[{ "title": "Our approach", "desc": "…" }]',
        validateItem: (it) => (it && typeof it.title === "string" && typeof it.desc === "string") ? "" : "Each card needs title and desc",
      },
    ],
  },
  {
    id: "about-whatwhy",
    title: "About — What we do / Why choose us",
    fields: [
      { key: "about.what_heading", label: "What we do — heading", type: "text", max: 120 },
      { key: "about.what_body",    label: "What we do — body",    type: "textarea", max: 800 },
      { key: "about.why_heading",  label: "Why choose us — heading", type: "text", max: 120 },
      { key: "about.why_body",     label: "Why choose us — body",    type: "textarea", max: 800 },
      { key: "about.why_points", label: "Why-choose-us bullet points (JSON list, first 4 shown)", type: "json", rows: 8,
        schemaHint: '[{ "title": "Quality and Reliability", "desc": "…" }]',
        validateItem: (it) => (it && typeof it.title === "string" && typeof it.desc === "string") ? "" : "Each point needs title and desc",
      },
    ],
  },
  {
    id: "products",
    title: "Products / Brands section",
    fields: [
      { key: "products.heading", label: "Section heading", type: "text", max: 120 },
      { key: "products.sub",     label: "Subheading",       type: "textarea", max: 280 },
    ],
  },
  {
    id: "services",
    title: "Services section",
    fields: [
      { key: "services.eyebrow", label: "Eyebrow", type: "text", max: 60 },
      { key: "services.heading", label: "Heading", type: "text", max: 120 },
      { key: "services.sub",     label: "Subheading", type: "textarea", max: 280 },
      { key: "services.cta_learn_more", label: "Learn-more CTA text", type: "text", max: 40 },
      { key: "services.mega_intro", label: "Mega-menu intro", type: "text", max: 200 },
      { key: "services.groups", label: "Service groups (JSON list)", type: "json", rows: 16,
        schemaHint: '[{ "title": "Digital Marketing", "blurb": "…", "items": ["A","B"] }]',
        validateItem: (it) => (it && typeof it.title === "string" && Array.isArray(it.items)) ? "" : "Each group needs title and items[]",
      },
    ],
  },
  {
    id: "industries",
    title: "Industries",
    fields: [
      { key: "industries.eyebrow", label: "Eyebrow", type: "text", max: 60 },
      { key: "industries.heading", label: "Heading", type: "text", max: 120 },
      { key: "industries.sub",     label: "Subheading", type: "textarea", max: 280 },
      { key: "industries.items", label: "Industries (JSON list)", type: "json", rows: 10,
        schemaHint: '[{ "title": "Hospitality", "desc": "…" }]',
        validateItem: (it) => (it && typeof it.title === "string") ? "" : "Each industry needs title",
      },
    ],
  },
  {
    id: "story",
    title: "Story Timeline (home)",
    fields: [
      { key: "story.timeline", label: "Story timeline (JSON object — eyebrow, heading, sub, milestones[])", type: "json", rows: 18, jsonType: "object",
        schemaHint: '{ "eyebrow": "Our Story", "heading": "…", "sub": "…", "milestones": [{ "year": "2006", "title": "Foundation", "desc": "…" }] }',
      },
    ],
  },
  {
    id: "portfolio",
    title: "Clients Portfolio page (hero + CTA)",
    fields: [
      { key: "portfolio.eyebrow", label: "Hero eyebrow (tiny uppercase line)", type: "text", max: 80 },
      { key: "portfolio.heading", label: "Hero heading (H1)", type: "text", max: 160 },
      { key: "portfolio.sub",     label: "Hero subheading", type: "textarea", max: 400 },
      { key: "portfolio.stats", label: "Hero stats (JSON list · exactly 3 entries)", type: "json", rows: 6,
        schemaHint: '[{ "value": "750+", "label": "Projects delivered" }]',
        validateItem: (it) => (it && typeof it.value === "string" && typeof it.label === "string") ? "" : "Each stat needs value and label",
      },
      { key: "portfolio.cta_eyebrow", label: "CTA eyebrow", type: "text", max: 60 },
      { key: "portfolio.cta_heading", label: "CTA heading", type: "text", max: 160 },
      { key: "portfolio.cta_body",    label: "CTA body",    type: "textarea", max: 400 },
      { key: "portfolio.cta_text",    label: "CTA button text", type: "text", max: 40 },
      { key: "portfolio.industries", label: "Filter tabs (JSON list of industry labels)", type: "json", rows: 6,
        schemaHint: '["All", "Commercial", "Healthcare", "Industrial", "Hospitality", "Data Centre", "Renewables"]',
        // Accept plain-string items OR { label } items
        validateItem: (it) => (typeof it === "string" && it.trim()) || (it && typeof it.label === "string" && it.label.trim()) ? "" : "Each entry must be a non-empty string (or { label })",
      },
      { key: "portfolio.count_label", label: 'Count line (use {shown} and {total})', type: "text", max: 80 },
    ],
  },
  {
    id: "career",
    title: "Career page / section",
    fields: [
      { key: "career.eyebrow",          label: "Eyebrow",         type: "text",     max: 60 },
      { key: "career.heading",          label: "Heading",         type: "text",     max: 140 },
      { key: "career.sub",              label: "Subheading",      type: "textarea", max: 400 },
      { key: "career.culture_title",    label: "Culture panel — title",  type: "text",     max: 120 },
      { key: "career.culture_body",     label: "Culture panel — body",   type: "textarea", max: 600 },
      { key: "career.open_roles_label", label: "Open roles label",       type: "text", max: 60 },
      { key: "career.cv_prompt",        label: "CV upload prompt",       type: "text", max: 120 },
      { key: "career.cta_apply",        label: "Apply CTA text",         type: "text", max: 40 },
      { key: "career.benefits", label: "Benefits (JSON list)", type: "json", rows: 8,
        schemaHint: '[{ "title": "Learning stipend", "desc": "…" }]',
        validateItem: (it) => (it && typeof it.title === "string") ? "" : "Each benefit needs a title",
      },
    ],
  },
  {
    id: "resources",
    title: "Resources / Blog section",
    fields: [
      { key: "resources.eyebrow",  label: "Eyebrow",     type: "text",     max: 60 },
      { key: "resources.heading",  label: "Heading",     type: "text",     max: 140 },
      { key: "resources.sub",      label: "Subheading",  type: "textarea", max: 400 },
      { key: "resources.cta_text", label: "CTA text",    type: "text",     max: 40 },
    ],
  },
  {
    id: "contact",
    title: "Contact popup (opens from Send Inquiry / Contact / final CTAs)",
    fields: [
      { key: "contact.eyebrow",         label: "Popup eyebrow — small uppercase caption above heading", type: "text", max: 60 },
      { key: "contact.heading",         label: "Popup heading — large title", type: "text", max: 120 },
      { key: "contact.sub",             label: "Popup subheading — one-line under heading", type: "textarea", max: 280 },
      { key: "contact.form_heading",    label: "Form heading (right column)", type: "text",     max: 80 },
      { key: "contact.form_note",       label: "Form note (small grey line)", type: "text",     max: 100 },
      { key: "contact.cta_submit",      label: "Submit button text", type: "text", max: 40 },
      // Advanced overrides — only needed if you want the popup to differ from
      // the on-page Contact section. Leave blank to inherit from the fields above.
      { key: "contact.dialog_eyebrow",  label: "(Optional) Popup-only eyebrow override",  type: "text",     max: 60 },
      { key: "contact.dialog_heading",  label: "(Optional) Popup-only heading override",  type: "textarea", max: 160 },
      { key: "contact.dialog_sub",      label: "(Optional) Popup-only subheading override", type: "textarea", max: 280 },
      // Legacy — used by the older on-page contact section if you ever re-enable it.
      { key: "contact.details_title",   label: "(Legacy) Details panel title", type: "text", max: 60 },
      { key: "contact.hours_title",     label: "(Legacy) Hours panel title", type: "text", max: 60 },
      { key: "contact.quick_title",     label: "(Legacy) Quick links title", type: "text", max: 60 },
      { key: "contact.map_label",       label: "(Legacy) Map label",  type: "text", max: 60 },
    ],
  },
  {
    id: "highlights",
    title: "Highlights / Trust",
    fields: [
      { key: "highlights.heading", label: "Big heading",      type: "text",     max: 140 },
      { key: "highlights.sub",     label: "Body paragraph",   type: "textarea", max: 600 },
      { key: "highlights.cards", label: "Capability cards (JSON list)", type: "json", rows: 12,
        schemaHint: '[{ "metric": "5", "title": "Global Offices", "desc": "USA · UAE · …" }]',
        validateItem: (it) => (it && typeof it.title === "string") ? "" : "Each card needs a title",
      },
      { key: "customers.title", label: "Customers panel title", type: "text", max: 160 },
      { key: "customers.list", label: "Customer base (JSON list)", type: "json", rows: 8,
        schemaHint: '[{ "count": "600+", "label": "System Integrators", "examples": "Telematics, …" }]',
        validateItem: (it) => (it && typeof it.label === "string") ? "" : "Each customer row needs a label",
      },
      { key: "geographic.title", label: "Geographic panel title", type: "text", max: 160 },
      { key: "geographic.list", label: "Geographic expansion (JSON list)", type: "json", rows: 8,
        schemaHint: '[{ "region": "Zambia, Africa", "detail": "Partner — …" }]',
        validateItem: (it) => (it && typeof it.region === "string") ? "" : "Each row needs a region",
      },
    ],
  },
  {
    id: "faq",
    title: "FAQ",
    fields: [
      { key: "faq.heading", label: "Big heading",  type: "text",     max: 140 },
      { key: "faq.sub",     label: "Subheading",   type: "textarea", max: 360 },
      { key: "faq.items", label: "FAQ items (JSON list)", type: "json", rows: 12,
        schemaHint: '[{ "q": "Question?", "a": "Answer." }]',
        validateItem: (it) => (it && typeof it.q === "string" && typeof it.a === "string") ? "" : "Each FAQ needs q (question) and a (answer)",
      },
    ],
  },
  {
    id: "locations",
    title: "Locations / Offices",
    fields: [
      { key: "locations.heading", label: "Big heading",  type: "text",     max: 140 },
      { key: "locations.sub",     label: "Subheading",   type: "textarea", max: 360 },
      { key: "locations.items", label: "Office locations (JSON list)", type: "json", rows: 14,
        schemaHint: '[{ "country": "UAE", "flag": "uae", "address": "Line 1\\nLine 2" }]',
        validateItem: (it) => (it && typeof it.country === "string" && typeof it.address === "string") ? "" : "Each office needs country and address (use \\n for new lines). Supported flag values: india, usa, uk, uae, bahrain, zambia, australia, canada",
      },
    ],
  },
  {
    id: "footer",
    title: "Footer",
    fields: [
      { key: "footer.desc",                label: "Description", type: "textarea", max: 280 },
      { key: "footer.quick_links_label",   label: "Quick links column title", type: "text", max: 60 },
      { key: "footer.services_label",      label: "Services column title", type: "text", max: 60 },
      { key: "footer.contact_label",       label: "Contact column title", type: "text", max: 60 },
      { key: "footer.locations_label",     label: "Locations column title", type: "text", max: 60 },
      { key: "footer.locations", label: "Office locations (JSON list)", type: "json", rows: 7,
        schemaHint: '[{ "name": "OpSys UAE", "city": "Dubai, UAE" }]',
        validateItem: (it) => (it && typeof it.name === "string") ? "" : "Each location needs a name",
      },
      { key: "footer.rights",              label: "Rights line", type: "text", max: 200 },
      { key: "footer.privacy",             label: "Privacy link text", type: "text", max: 40 },
      { key: "footer.terms",               label: "Terms link text", type: "text", max: 40 },
    ],
  },
];

const ALL_FIELDS = GROUPS.flatMap((g) => g.fields);

function validateField(field, value) {
  if (field.type === "image") return "";
  if (field.type === "json") {
    if (!value || !value.trim()) return ""; // empty -> falls back to defaults
    let parsed;
    try { parsed = JSON.parse(value); }
    catch { return "Invalid JSON syntax"; }
    // Field opt-in: JSON blob may be a plain object (e.g. story.timeline)
    if (field.jsonType === "object") {
      if (Array.isArray(parsed) || typeof parsed !== "object" || parsed === null) return "Value must be a JSON object";
      return field.validateItem ? (field.validateItem(parsed) || "") : "";
    }
    if (!Array.isArray(parsed)) return "Value must be a JSON array";
    if (field.validateItem) {
      for (let i = 0; i < parsed.length; i++) {
        const err = field.validateItem(parsed[i]);
        if (err) return `Item #${i + 1}: ${err}`;
      }
    }
    return "";
  }
  return runValidators(value, [validators.maxLen(field.max || 500)]);
}

function prettyJSON(raw) {
  if (!raw) return raw;
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
}

export default function PageContentManager() {
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState({});
  const [openGroup, setOpenGroup] = useState("brand");

  useEffect(() => {
    adminApi.get("/admin/page-content").then((r) => {
      // Pretty print JSON-typed values for editing comfort
      const next = { ...r.data };
      for (const f of ALL_FIELDS) {
        if (f.type === "json" && next[f.key]) next[f.key] = prettyJSON(next[f.key]);
      }
      setData(next);
    });
  }, []);

  const set = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: "" }));
  };

  const saveOne = async (field) => {
    const value = data[field.key] || "";
    const err = validateField(field, value);
    if (err) {
      setErrors((e) => ({ ...e, [field.key]: err }));
      toast.error(err);
      return;
    }
    let toSave = value;
    if (field.type === "json" && value.trim()) {
      // Re-stringify to compact form before persisting (safer & smaller)
      toSave = JSON.stringify(JSON.parse(value));
    }
    setSaving((s) => ({ ...s, [field.key]: true }));
    try {
      await adminApi.put("/admin/page-content", { key: field.key, value: toSave });
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving((s) => ({ ...s, [field.key]: false }));
    }
  };

  const saveAll = async () => {
    const newErr = {};
    for (const f of ALL_FIELDS) {
      const e = validateField(f, data[f.key] || "");
      if (e) newErr[f.key] = e;
    }
    setErrors(newErr);
    if (Object.keys(newErr).length) {
      toast.error("Some fields are invalid");
      return;
    }
    try {
      for (const f of ALL_FIELDS) {
        let v = data[f.key] || "";
        if (f.type === "json" && v.trim()) v = JSON.stringify(JSON.parse(v));
        await adminApi.put("/admin/page-content", { key: f.key, value: v });
      }
      toast.success("All saved");
    } catch { toast.error("Save failed"); }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Page Content"
        subtitle="Edit every heading, subhead, CTA and structured list (services, industries, hero stats…) shown on the public site. Empty values fall back to the built-in defaults."
        action={<Button onClick={saveAll} className="text-white" style={{ background: "#1B6FE0" }} data-testid="content-save-all">Save all</Button>}
      />

      <div className="space-y-3" data-testid="content-groups">
        {GROUPS.map((g) => {
          const open = openGroup === g.id;
          return (
            <Card key={g.id} className="overflow-hidden" data-testid={`content-group-${g.id}`}>
              <button
                onClick={() => setOpenGroup(open ? null : g.id)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition"
              >
                <span className="font-heading font-semibold text-[15px] tracking-tight" style={{ color: "#0F2A6E" }}>{g.title}</span>
                <CaretDown size={14} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="px-6 pb-6 pt-1 space-y-4 border-t border-slate-100">
                  {g.fields.map((f) => (
                    <div key={f.key} data-testid={`content-${f.key}`}>
                      <div className="flex items-baseline justify-between">
                        <Label>{f.label}</Label>
                        {f.type !== "json" && f.type !== "image" && (
                          <span className="text-[10.5px] text-slate-400 tabular-nums">
                            {(data[f.key] || "").length}/{f.max}
                          </span>
                        )}
                      </div>
                      {f.type === "image" && (
                        <div className="mt-2">
                          <ImageUpload
                            value={data[f.key] || null}
                            onChange={(id) => {
                              set(f.key, id || "");
                              // auto-save image field so it persists immediately
                              adminApi.put("/admin/page-content", { key: f.key, value: id || "" })
                                .then(() => toast.success("Image saved"))
                                .catch(() => toast.error("Save failed"));
                            }}
                            label=""
                            testId={`content-image-${f.key}`}
                          />
                        </div>
                      )}
                      {f.type === "textarea" && (
                        <Textarea rows={3} className="mt-1.5" maxLength={(f.max || 500) + 50} value={data[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
                      )}
                      {f.type === "text" && (
                        <Input className="mt-1.5" maxLength={(f.max || 200) + 50} value={data[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
                      )}
                      {f.type === "json" && (
                        <>
                          <Textarea
                            rows={f.rows || 8}
                            className="mt-1.5 font-mono text-[12.5px]"
                            value={data[f.key] || ""}
                            onChange={(e) => set(f.key, e.target.value)}
                            placeholder={f.schemaHint}
                            data-testid={`content-json-${f.key}`}
                          />
                          {f.schemaHint && (
                            <div className="text-[11px] text-slate-400 mt-1">Format: <code className="bg-slate-100 px-1 rounded">{f.schemaHint}</code></div>
                          )}
                        </>
                      )}
                      {errors[f.key] && <div className="text-[12px] mt-1" style={{ color: "#1B6FE0" }}>{errors[f.key]}</div>}
                      {f.type !== "image" && (
                        <div className="mt-1.5 flex justify-end">
                          <button
                            onClick={() => saveOne(f)}
                            disabled={saving[f.key]}
                            className="text-[12px] font-medium disabled:opacity-50"
                            style={{ color: "#1B6FE0" }}
                            data-testid={`content-save-${f.key}`}
                          >
                            {saving[f.key] ? "Saving…" : "Save"}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </PageContainer>
  );
}
